import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useLabels, useRequestData } from 'wsm-common-data';
import { useSelector, useDispatch } from 'react-redux';
import { setClassNames } from 'ddc-classnames-js';
import { trackEvent } from 'ddc-track-event';

const LayoutToggle = ({ portal, isPortalReady }) => {
	const labels = useLabels();
	const { widgetName, windowId } = useRequestData();
	const {
		layout: { layout }
	} = useSelector((state) => state);
	const dispatch = useDispatch();

	const isGridView = layout === 'grid';

	const sharedButtonClasses = [
		'text-link',
		'text-link-muted',
		'px-3',
		'line-height-condensed',
		'text-no-decoration'
	];
	const listViewClasses = ['border-right', !isGridView ? 'active' : null];
	const gridViewClasses = [isGridView ? 'active' : null];

	const handleToggle = (type = 'list') => {
		const layoutType = localStorage.getItem('srpLayout');
		if (!layoutType || layoutType !== type) {
			localStorage.setItem('srpLayout', type);
			dispatch({ type: 'SET_LAYOUT', payload: { layout: type } });

			trackEvent(widgetName, windowId, {
				element: `${type[0].toUpperCase()}${type.slice(
					1
				)} layout button`,
				result: `Results updated to ${type} layout`
			});

			if (window && window.DDC && window.DDC.pubsub) {
				window.DDC.pubsub.publish('ws-inv-data/toggleListingView');
			}
		}
	};

	const LayoutToggleToolbar = (
		<div
			className="d-none d-md-block ml-4"
			role="toolbar"
			aria-label={labels.get('CHANGE_LAYOUT')}
			// Overrides baseline so that icons center with surrounding text and elements.
			style={{ transform: 'translateY(.3rem)' }}
		>
			<button
				className={setClassNames([
					...sharedButtonClasses,
					...listViewClasses
				])}
				aria-label={
					!isGridView
						? labels.get('LIST_VIEW')
						: labels.get('SWITCH_TO_LIST_VIEW')
				}
				aria-pressed={!isGridView}
				onClick={() => handleToggle('list')}
				type="button"
			>
				<i
					className="ddc-icon ddc-icon-size-large ddc-icon-layout-list"
					aria-hidden="true"
				/>
			</button>
			<button
				className={setClassNames([
					...sharedButtonClasses,
					...gridViewClasses
				])}
				aria-label={
					isGridView
						? labels.get('GRID_VIEW')
						: labels.get('SWITCH_TO_GRID_VIEW')
				}
				aria-pressed={isGridView}
				onClick={() => handleToggle('grid')}
				type="button"
			>
				<i
					className="ddc-icon ddc-icon-size-large ddc-icon-layout-grid"
					aria-hidden="true"
				/>
			</button>
		</div>
	);
	if (!portal) {
		return LayoutToggleToolbar;
	}

	if (isPortalReady) {
		const portalElement = document.getElementById(
			'card-layout-toggle-portal'
		);

		// Renders the `layoutToggle` into a portal
		if (portal && portalElement) {
			return ReactDOM.createPortal(LayoutToggleToolbar, portalElement);
		}
	}

	return null;
};

LayoutToggle.propTypes = {
	portal: PropTypes.bool,
	isPortalReady: PropTypes.bool
};

LayoutToggle.defaultProps = {
	portal: false,
	isPortalReady: false
};

export default LayoutToggle;
